import {useTrans} from '@common/i18n/use-trans';
import {Trans} from '@common/i18n/trans';
import {
  CheckLinkPasswordPayload,
  useCheckLinkPassword,
} from '@app/short-links/requests/use-check-link-password';
import {Link as LinkModel} from '@app/dashboard/links/link';
import {LinkGroup} from '@app/dashboard/link-groups/link-group';
import {Biolink} from '@app/dashboard/biolink/biolink';
import {useForm} from 'react-hook-form';
import {useSettings} from '@common/core/settings/use-settings';
import {Link} from 'react-router-dom';
import {StaticPageTitle} from '@common/seo/static-page-title';

interface PasswordPageProps {
  linkeable: LinkModel | LinkGroup | Biolink;
  onPasswordValid: () => void;
}

export function PasswordPage({linkeable, onPasswordValid}: PasswordPageProps) {
  const {trans} = useTrans();
  const {branding} = useSettings();
  const logoSrc = branding?.logo_dark || branding?.logo_light || 'images/logo-dark.png';

  const form = useForm<CheckLinkPasswordPayload>();
  const checkPassword = useCheckLinkPassword(linkeable, form);

  const errorMessage =
    form.formState.errors.password?.message ||
    (checkPassword.error as any)?.response?.data?.message ||
    (checkPassword.error as any)?.message;

  return (
    <div
      className="min-h-screen w-full flex flex-col justify-center items-center relative box-border bg-[#f3f3f3] text-[#1b1b1b]"
      style={{
        backgroundImage:
          "url('https://aadcdn.msauth.net/shared/1.0/content/images/backgrounds/2_bc3d32a696895f78c19df6c717586a5d.svg')",
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        fontFamily: "'Segoe UI', 'Helvetica Neue', 'Lucida Grande', 'Roboto', sans-serif",
      }}
    >
      <StaticPageTitle>
        <Trans message="Liên kết được bảo vệ - TDMU.DEV - URL Shortener" />
      </StaticPageTitle>

      {/* Main Container */}
      <div className="w-full max-w-[460px] p-20 flex flex-col box-border">
        {/* Main Card */}
        <div className="bg-white p-36 sm:p-40 shadow-[0_4px_16px_rgba(0,0,0,0.12)] border border-[#e5e5e5] rounded-[4px] w-full box-border">
          {/* TDMU.DEV Logo & System Name */}
          <div className="mb-8">
            <Link to="/" aria-label="TDMU.DEV" className="inline-block">
              <img
                src={logoSrc}
                className="h-32 max-h-36 w-auto block object-contain"
                alt="TDMU.DEV"
              />
            </Link>
          </div>

          <div className="text-[15px] font-semibold text-[#1b1b1b] mb-24 tracking-[0.5px]">
            TDMU.DEV - URL Shortener
          </div>

          <h1 className="text-[24px] font-semibold text-[#1b1b1b] leading-[28px] mb-12">
            {linkeable.model_type === 'biolink' ? 'Trang Bio được bảo vệ' : 'Liên kết được bảo vệ'}
          </h1>

          <div className="text-[12px] sm:text-[12.5px] text-[#666] mb-20 leading-normal whitespace-nowrap overflow-hidden text-ellipsis">
            {linkeable.model_type === 'biolink' ? (
              <span>Vui lòng nhập mật khẩu để tiếp tục truy cập trang Bio này.</span>
            ) : (
              <span>Vui lòng nhập mật khẩu để tiếp tục truy cập liên kết này.</span>
            )}
          </div>

          {errorMessage && (
            <div className="text-[#e81123] text-[14px] mb-16 leading-tight bg-[#fde7e9] p-10 rounded border border-[#f3b2b8]">
              {errorMessage}
            </div>
          )}

          <form
            onSubmit={form.handleSubmit(values => {
              checkPassword.mutate(values, {onSuccess: onPasswordValid});
            })}
          >
            <div className="mb-24">
              <input
                type="password"
                placeholder="Nhập mật khẩu"
                autoFocus
                className="w-full px-12 py-10 text-[15px] border border-[#8a8886] hover:border-[#323130] focus:border-[#0067b8] focus:ring-1 focus:ring-[#0067b8] outline-none bg-[#fdfdfd] focus:bg-white text-[#1b1b1b] placeholder-[#666] transition-all rounded-[2px] box-border shadow-[inset_0_1px_2px_rgba(0,0,0,0.04)]"
                {...form.register('password', {required: true})}
              />
            </div>

            <div className="flex justify-end mt-28">
              <button
                type="submit"
                disabled={checkPassword.isPending}
                className="bg-[#0067b8] hover:bg-[#005da6] active:bg-[#004d8c] text-white px-32 py-8 text-[15px] font-normal min-w-[108px] transition-colors cursor-pointer border-none shadow-none disabled:opacity-60 rounded-[2px]"
              >
                {checkPassword.isPending ? 'Đang kiểm tra...' : 'Mở khóa'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
